'use client';

import { Fragment, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import AnchorLink from '@/components/TableOfContents/AnchorLink';
import { useForm } from 'react-hook-form';
import { InputMask } from '@react-input/mask';
import {
  getPatterns,
  getMinValues,
  getMaxValues,
  stateOptions,
} from '@/utils/formUtils';
import { analytics } from '@/utils/analytics';
import { submitCrossmodForm } from '@/services/formService';
import crossmodFormImg from '@/assets/images/crossmod-form-img.webp';

interface FormSection {
  title?: string;
  type: string;
  fields: {
    input: string;
    title: string;
    required?: boolean;
    defaultOption?: string;
    options?: any[];
  }[];
}

interface Props {
  image: {
    url: string;
    alt: string;
  };
  imageHeading: string;
  heading?: string;
  description?: string;
  formSections: FormSection[];
  disclaimer: string;
  includeSegment?: boolean;
}

const CtaForm = ({
  // image,
  imageHeading,
  // heading,
  description,
  formSections,
  disclaimer,
  includeSegment = false,
}: Props) => {
  const [isFormSending, setIsFormSending] = useState(false);
  const [formStatus, setFormStatus] = useState<'success' | 'error' | null>(
    null
  );
  const anchorTagName = 'crossmod-form';

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();

  const generateErrorMsg = (errors, input) => {
    let hasErrorProperty = Object.prototype.hasOwnProperty.call(errors, input);
    if (hasErrorProperty) {
      return (
        <p className="absolute bottom-0 left-0 translate-y-3 pt-1 text-[0.85rem] leading-4 text-error-message">
          {errors[input].message}
        </p>
      );
    }
  };

  const generateFormFields = (section) => {
    switch (section.type) {
      case 'text':
        return (
          section?.fields && (
            <div className="mb-3 grid grid-cols-1 gap-4 [@media(min-width:600px)]:grid-cols-2">
              {section.fields.map((field, key) => (
                <div className="relative" key={key}>
                  {field?.input !== 'phone' && field?.input !== 'state' && (
                    <input
                      {...register(field?.input, {
                        required: field?.required
                          ? `${field?.title} is required.`
                          : false,
                        pattern: getPatterns(field?.input),
                        minLength: getMinValues(field?.input),
                        maxLength: getMaxValues(field?.input),
                      })}
                      placeholder={`${field?.title}${field?.required ? '*' : ''}`}
                      type={section?.type}
                      className="my-2 w-full border-b-[1px] bg-white p-2"
                    />
                  )}
                  {field?.input === 'phone' && (
                    <InputMask
                      {...register(field?.input, {
                        required: field?.required
                          ? `${field?.title} is required.`
                          : false,
                        pattern: getPatterns(field?.input),
                        minLength: getMinValues(field?.input),
                        maxLength: getMaxValues(field?.input),
                      })}
                      mask="___-___-____"
                      placeholder={`${field?.title}${field?.required ? '*' : ''}`}
                      replacement="_"
                      type="text"
                      className="my-2 w-full border-b-[1px] bg-white p-2"
                    />
                  )}
                  {field?.input === 'state' && (
                    <div className="w-full">
                      <select
                        {...register(field?.input, {
                          required: field?.required
                            ? `${field?.title} is required.`
                            : false,
                          pattern: getPatterns(field?.input),
                          minLength: getMinValues(field?.input),
                          maxLength: getMaxValues(field?.input),
                        })}
                        name={field?.input}
                        defaultValue=""
                        className="my-2 w-full border-b-[1px] px-2 py-[0.6rem] text-grey-1"
                      >
                        <option disabled hidden value="">
                          {field?.title}
                          {field?.required && '*'}
                        </option>
                        {stateOptions?.map((state, index) => (
                          <option value={state?.abbreviation} key={index}>
                            {state?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {errors && generateErrorMsg(errors, field?.input)}
                </div>
              ))}
            </div>
          )
        );
      case 'radio':
        return (
          section?.fields && (
            <div className="relative mb-4 flex w-full flex-col text-left text-grey-1 [@media(min-width:600px)]:flex-row">
              {section.fields.map((field, key) => (
                <Fragment key={key}>
                  <p className="mr-4">{`${field?.title}${section?.required ? '*' : ''}`}</p>
                  <div className="inline-flex">
                    <input
                      {...register(field?.input, {
                        required: section?.required,
                      })}
                      value="Yes"
                      type={section?.type}
                      className="ml-3 mr-1"
                    />
                    <p>Yes</p>
                    <input
                      {...register(field?.input, {
                        required: section?.required,
                      })}
                      value="No"
                      type={section?.type}
                      className="ml-3 mr-1"
                    />
                    <p>No</p>
                  </div>
                  {errors && generateErrorMsg(errors, field?.input)}
                </Fragment>
              ))}
            </div>
          )
        );
      case 'select':
        return (
          <div className="relative flex w-full flex-col justify-between text-grey-1 [@media(min-width:600px)]:flex-row [@media(min-width:600px)]:gap-4">
            {section?.fields?.map((field, index) => (
              <Fragment key={index}>
                <label>
                  {`${field?.title}${section?.required ? '*' : ''}`}
                </label>
                <div>
                  <select
                    {...register(field?.input, {
                      required: section?.required,
                    })}
                    defaultValue=""
                    className="mb-2 w-full border-b-[1px] p-2 text-grey-1"
                  >
                    <option disabled hidden value="">
                      {field?.defaultOption}
                    </option>
                    {field?.options?.map((option, key) => (
                      <option value={option?.value} key={key}>
                        {option?.label}
                      </option>
                    ))}
                  </select>
                </div>
                {errors && generateErrorMsg(errors, field?.input)}
              </Fragment>
            ))}
          </div>
        );
      default:
        return;
    }
  };

  const onSubmit = async () => {
    const formValues = getValues();
    setIsFormSending(true);
    await submitCrossmodForm(formValues).then((status) => {
      if (status === 200) {
        setFormStatus('success');
        includeSegment &&
          analytics?.track('Generic Contact Form Submitted', {
            formId: 'crossmod-form',
            email: formValues.email,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            phone: formValues?.phone || '',
          });
      } else {
        setFormStatus('error');
      }
    });
  };

  // As we draw closer to Event Bridge, we can start looking at
  // passing parameters to determine whether a form is using deprecated
  // endpoints and appending the below hidden inputs/routes conditionally
  return (
    <section>
      <AnchorLink anchorTagRef={`#${anchorTagName}`} />
      <div className="mb-[30px] grid w-full grid-cols-1 grid-rows-[auto_1fr] bg-[#F3F5F7] smDesktop:grid-cols-2 smDesktop:grid-rows-1">
        <div className="relative -mb-[5px] h-full max-h-[450px] w-full pt-[75%] text-center smDesktop:max-h-[unset] smDesktop:pt-0 [@media(min-width:600px)]:pt-[60%]">
          <Image
            className="h-full w-full"
            src={crossmodFormImg}
            alt="Cta Form Image"
            fill
            objectFit="cover"
          />
          <div className="absolute left-0 top-0 z-[2] w-full p-8 text-center font-acumin !text-[2.125rem] font-bold leading-10 text-white drop-shadow-lg md:top-0 md:px-14 md:py-6 md:text-[2.5rem] md:leading-[3rem]">
            {imageHeading}
          </div>
        </div>
        <div className="mx-auto mt-[30px] w-mobile md:w-3/5 smDesktop:w-mobile">
          <h3 className="mb-7 font-acumin text-[1.375rem] font-bold leading-[1.625rem] -tracking-[0.025em] md:text-[1.563rem] md:leading-[1.875rem]">
            If you&apos;re interested in developing with CrossMod homes, contact
            us at{' '}
            <Link href="mailto:crossmodteam@claytonhomes.com">
              crossmodteam@claytonhomes.com
            </Link>{' '}
            or complete the form below to schedule a CrossMod consultation.
          </h3>
          {description && (
            <p className="my-4 font-source-sans text-[0.875rem] leading-5 text-grey-1">
              {description}
            </p>
          )}
          {formStatus === 'success' && (
            <div className="py-[90px] text-[30px] font-semibold text-green-500">
              We received your request. Thank you.
            </div>
          )}
          {formStatus === 'error' && (
            <div className="pt-[90px] text-[30px] font-semibold text-red-500">
              Error. Sorry. We could not process your request.
            </div>
          )}
          {!formStatus && formSections && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              autoComplete="on"
              id="crossmod-cta-form"
              name="crossmodForm"
            >
              {formSections.map((section, key: number) => (
                <div key={key}>
                  {section?.title && <h4 className="mb-2">{section.title}</h4>}
                  {generateFormFields(section)}
                </div>
              ))}
              {disclaimer && (
                <p className="mt-4 font-source-sans text-[0.75rem] leading-4 text-grey-1">
                  {disclaimer}
                </p>
              )}
              <div className="mx-auto my-[30px] w-full cursor-pointer rounded-[3px] border-none p-0">
                <button
                  type="submit"
                  id="submit-button"
                  className="w-full cursor-pointer rounded-[3px] border-none bg-clayton-blue px-[30px] py-[13px] font-source-sans text-[18px] font-semibold text-white disabled:bg-[#888888] disabled:opacity-50"
                  disabled={isFormSending}
                >
                  {isFormSending ? 'SENDING . . .' : 'SUBMIT'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default CtaForm;
